import React, { useCallback, useEffect, useState, useRef } from "react";
import { Button, Card, Col, Form, Row, Modal, Tab, Nav } from "react-bootstrap";
import { Controller, Resolver, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useNavigate, useParams } from "react-router-dom";
import * as yup from "yup";
import BreadcrumbItem from "../../../shared/components/BreadcrumbItem";
import { useTranslation } from "react-i18next";
import i18n from "../../../utils/localization/i18n";
import { enqueueSnackbar } from "notistack";
import { handleError } from "../../../shared/functions/handleError";
import { InstitutionService } from "../../institutions/service";
import { ProtocolService } from "../service";
import { IncidentService } from "../../incidents/service";
import { IInstitution } from "../../../shared/interfaces/institution/IInstitution";
import { IProtocolItem, MediaType, IProtocolItemContent } from "../../../shared/interfaces/protocol/IProtocolItem";
import AsyncSelect2 from "../../../shared/components/AsyncSelect2";
import { ISelectOption } from "../../../shared/interfaces/shared/ISelectOption";
import Select2 from "../../../shared/components/Select2";
import { DragDropContext, Droppable, Draggable, DropResult } from "@hello-pangea/dnd";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { BASE_URL } from "../../../utils/api/endpoints";
import { instance } from "../../../utils/api/api.service";

interface IProtocolForm {
    id?: number;
    title: string;
    note?: string;
    incidentType?: string;
    institutionId?: number;
}

interface IItemFormData {
    text: string;
    mediaType: MediaType;
    contents: {
        [language: string]: IProtocolItemContent;
    };
}

const protocolSchema = yup.object().shape({
    title: yup
        .string()
        .required("validation.required")
        .min(2, "validation.minLength")
        .max(100, "validation.maxLength")
        .trim(),
    note: yup.string().max(1000, "validation.maxLength").trim(),
    incidentType: yup.string().optional(),
    institutionId: yup.number().optional(),
});

const initialValues: Partial<IProtocolForm> = {
    title: "",
    note: "",
    incidentType: "",
    institutionId: undefined,
};

const initialItemFormData: IItemFormData = {
    text: "",
    mediaType: "TEXT",
    contents: {
        de: { text: "" },
        en: { text: "" },
    },
};

const quillModules = {
    toolbar: [
        [{ header: [1, 2, 3, false] }],
        ["bold", "italic", "underline", "strike"],
        [{ list: "ordered" }, { list: "bullet" }],
        ["link"],
        ["clean"],
    ],
};

const quillFormats = [
    "header",
    "bold",
    "italic",
    "underline",
    "strike",
    "list",
    "bullet",
    "link",
];

const ProtocolForm = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { protocolId } = useParams();
    const [defaultInstitutionOptions, setDefaultInstitutionOptions] = useState<ISelectOption<number>[]>([]);
    const [protocolItems, setProtocolItems] = useState<IProtocolItem[]>([]);
    const [isItemModalOpen, setIsItemModalOpen] = useState(false);
    const [editingItem, setEditingItem] = useState<IProtocolItem | null>(null);
    const [itemFormData, setItemFormData] = useState<IItemFormData>(initialItemFormData);
    const [isLoadingItems, setIsLoadingItems] = useState(false);
    const [isSavingItem, setIsSavingItem] = useState(false);
    const [languageFiles, setLanguageFiles] = useState<{ [lang: string]: File[] }>({
        de: [],
        en: [],
    });
    const germanFileInputRef = useRef<HTMLInputElement>(null);
    const englishFileInputRef = useRef<HTMLInputElement>(null);
    const [isTranslating, setIsTranslating] = useState(false);
    const [incidentTypes, setIncidentTypes] = useState<string[]>([]);

    // Inject styles for rich text preview
    useEffect(() => {
        const styleId = 'protocol-preview-styles';
        if (!document.getElementById(styleId)) {
            const style = document.createElement('style');
            style.id = styleId;
            style.textContent = `
                .protocol-preview-text p {
                    margin: 0 0 8px 0 !important;
                }
                .protocol-preview-text p:last-child {
                    margin-bottom: 0 !important;
                }
                .protocol-preview-text strong,
                .protocol-preview-text b {
                    font-weight: 700 !important;
                    color: #333 !important;
                }
                .protocol-preview-text em,
                .protocol-preview-text i {
                    font-style: italic !important;
                }
                .protocol-preview-text u {
                    text-decoration: underline !important;
                }
                .protocol-preview-text s,
                .protocol-preview-text strike {
                    text-decoration: line-through !important;
                }
                .protocol-preview-text h1 {
                    font-size: 18px !important;
                    font-weight: 700 !important;
                    margin: 12px 0 8px 0 !important;
                    color: #1a1a2e !important;
                }
                .protocol-preview-text h2 {
                    font-size: 16px !important;
                    font-weight: 700 !important;
                    margin: 10px 0 6px 0 !important;
                    color: #1a1a2e !important;
                }
                .protocol-preview-text h3 {
                    font-size: 14px !important;
                    font-weight: 700 !important;
                    margin: 8px 0 4px 0 !important;
                    color: #1a1a2e !important;
                }
                .protocol-preview-text ul,
                .protocol-preview-text ol {
                    margin: 4px 0 !important;
                    padding-left: 20px !important;
                }
                .protocol-preview-text li {
                    margin: 2px 0 !important;
                }
                .protocol-preview-text a {
                    color: #c62828 !important;
                    text-decoration: underline !important;
                }
            `;
            document.head.appendChild(style);
        }
        
        return () => {
            const existingStyle = document.getElementById(styleId);
            if (existingStyle) {
                existingStyle.remove();
            }
        };
    }, []);

    // Helper to get video/image URL from item
    const getMediaUrl = (item: IProtocolItem): string | null => {
        // Try current language first, then fallback
        const currentLang = i18n.language === "de" ? "de" : "en";
        const fallbackLang = currentLang === "de" ? "en" : "de";
        
        const currentContent = item.contents?.[currentLang]?.files?.[0];
        const fallbackContent = item.contents?.[fallbackLang]?.files?.[0];
        const url = currentContent?.contentUrl || fallbackContent?.contentUrl || null;
        
        if (!url) return null;
        
        // If already a full URL, return as-is
        if (url.startsWith('http://') || url.startsWith('https://')) {
            return url;
        }
        
        // Convert relative URL to full URL
        if (url.startsWith('/')) {
            const baseUrlWithoutApi = (BASE_URL || '').replace(/\/api\/v\d+/, '');
            return `${baseUrlWithoutApi}${url}`;
        }
        
        return url;
    };

    const incidentTypeOptions = incidentTypes.map((type) => ({
        value: type,
        label: t(`incident.type.${type}`),
    }));

    const {
        control,
        handleSubmit,
        formState: { errors, isSubmitting },
        reset,
    } = useForm<IProtocolForm>({
        resolver: yupResolver(protocolSchema) as unknown as Resolver<IProtocolForm>,
        defaultValues: initialValues as IProtocolForm,
    });

    // Load institutions from API with search query
    const loadInstitutions = useCallback(async (inputValue: string): Promise<ISelectOption<number>[]> => {
        try {
            const response = await InstitutionService.getAll({
                page: 0,
                size: 50,
                name: inputValue || undefined,
            });
            return (response?.data || []).map((inst: IInstitution) => ({
                value: inst.id!,
                label: inst.name,
            }));
        } catch (error) {
            handleError(error);
            return [];
        }
    }, []);

    // Load initial default options
    useEffect(() => {
        loadInstitutions("").then(setDefaultInstitutionOptions);
    }, [loadInstitutions]);

    // Load incident types from API
    useEffect(() => {
        IncidentService.getTypes()
            .then((response) => {
                setIncidentTypes(response?.data || []);
            })
            .catch(handleError);
    }, []);

    // Load protocol data if editing
    useEffect(() => {
        if (protocolId) {
            ProtocolService.getSingle(Number(protocolId))
                .then((response) => {
                    const data = response?.data;
                    reset(data);
                    
                    // Add institution to default options if exists
                    if (data?.institutionId && data?.institutionName) {
                        setDefaultInstitutionOptions([{
                            value: data.institutionId,
                            label: data.institutionName
                        }]);
                    }
                })
                .catch(handleError);

            // Load protocol items
            setIsLoadingItems(true);
            ProtocolService.getItems(Number(protocolId))
                .then((response) => {
                    setProtocolItems(response?.data || []);
                })
                .catch(handleError)
                .finally(() => setIsLoadingItems(false));
        }
    }, [protocolId, reset]);

    // Handle drag end for reordering
    const handleDragEnd = (result: DropResult) => {
        if (!result.destination) return;

        const items = Array.from(protocolItems);
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);

        // Update order for all items
        const updatedItems = items.map((item, index) => ({
            ...item,
            order: index,
        }));

        setProtocolItems(updatedItems);

        // Call API to update order if protocol exists
        if (protocolId) {
            const sortPayload = updatedItems.map((item, index) => ({
                id: item.id!,
                sortOrder: index,
            }));
            ProtocolService.sortItems(Number(protocolId), sortPayload)
                .then(() => {
                    enqueueSnackbar(t("protocol.itemsReordered"), { variant: "success" });
                })
                .catch(handleError);
        }
    };

    // Open modal for adding new item
    const handleAddItem = () => {
        setEditingItem(null);
        setItemFormData(initialItemFormData);
        setLanguageFiles({ de: [], en: [] });
        setIsItemModalOpen(true);
    };

    // Open modal for editing existing item
    const handleEditItem = (item: IProtocolItem) => {
        setEditingItem(item);
        setItemFormData({
            text: item.text,
            mediaType: item.mediaType,
            contents: item.contents || { de: { text: "" }, en: { text: "" } },
        });
        setLanguageFiles({ de: [], en: [] });
        setIsItemModalOpen(true);
    };

    // Delete item
    const handleDeleteItem = async (itemId: number) => {
        // If no protocolId, just remove locally
        if (!protocolId) {
            setProtocolItems((prev) => prev.filter((item) => item.id !== itemId));
            return;
        }

        try {
            await ProtocolService.deleteItem(itemId);
            setProtocolItems((prev) => prev.filter((item) => item.id !== itemId));
            enqueueSnackbar(t("toasts.successfullyDeleted", { name: t("protocol.procedureItems") }), { variant: "success" });
        } catch (error) {
            handleError(error);
        }
    };

    // Generate temporary ID for local items
    const generateTempId = () => -Date.now();

    // AI Translation handler
    const handleAITranslate = async (fromLang: string, toLang: string) => {
        const sourceText = itemFormData.contents?.[fromLang]?.text;
        if (!sourceText || sourceText.trim() === "" || sourceText === "<p><br></p>") {
            enqueueSnackbar(t("protocol.noTextToTranslate"), { variant: "warning" });
            return;
        }

        setIsTranslating(true);
        try {
            const response = await instance.post(`${BASE_URL}/web/ai-translation`, {
                text: sourceText,
                language: toLang,
                preserveHtml: true
            });
            
            const translatedText = response.data;
            setItemFormData((prev) => ({
                ...prev,
                contents: {
                    ...prev.contents,
                    [toLang]: { ...prev.contents?.[toLang], text: translatedText },
                },
            }));
            enqueueSnackbar(t("protocol.translationSuccess"), { variant: "success" });
        } catch (error) {
            handleError(error);
            enqueueSnackbar(t("protocol.translationFailed"), { variant: "error" });
        } finally {
            setIsTranslating(false);
        }
    };

    // Save item from modal
    const handleSaveItem = async () => {
        if (!itemFormData.text.trim()) {
            enqueueSnackbar(t("validation.required"), { variant: "error" });
            return;
        }

        // Validate both language contents for TEXT type
        if (itemFormData.mediaType === "TEXT") {
            const deText = itemFormData.contents?.de?.text;
            const enText = itemFormData.contents?.en?.text;
            const isDeEmpty = !deText || deText.trim() === "" || deText === "<p><br></p>";
            const isEnEmpty = !enText || enText.trim() === "" || enText === "<p><br></p>";
            
            if (isDeEmpty || isEnEmpty) {
                enqueueSnackbar(t("protocol.bothLanguagesRequired"), { variant: "error" });
                return;
            }
        }

        // Validate both language files for IMAGE and VIDEO types
        if (itemFormData.mediaType === "IMAGE" || itemFormData.mediaType === "VIDEO") {
            const hasDeFiles = languageFiles.de.length > 0 || (itemFormData.contents?.de?.files && itemFormData.contents.de.files.length > 0);
            const hasEnFiles = languageFiles.en.length > 0 || (itemFormData.contents?.en?.files && itemFormData.contents.en.files.length > 0);
            
            if (!hasDeFiles || !hasEnFiles) {
                enqueueSnackbar(t("protocol.bothLanguagesRequired"), { variant: "error" });
                return;
            }
        }

        // If no protocolId, save locally with temporary ID
        if (!protocolId) {
            if (editingItem) {
                setProtocolItems((prev) =>
                    prev.map((item) =>
                        item.id === editingItem.id
                            ? { ...item, ...itemFormData }
                            : item
                    )
                );
            } else {
                const newItem: IProtocolItem = {
                    id: generateTempId(),
                    text: itemFormData.text,
                    mediaType: itemFormData.mediaType,
                    protocolId: 0,
                    contents: itemFormData.contents,
                };
                setProtocolItems((prev) => [...prev, newItem]);
            }
            setIsItemModalOpen(false);
            setItemFormData(initialItemFormData);
            setEditingItem(null);
            return;
        }

        setIsSavingItem(true);
        try {
            const payload = {
                text: itemFormData.text,
                mediaType: itemFormData.mediaType,
                protocolId: Number(protocolId),
                contents: itemFormData.contents,
            };

            let savedItem: IProtocolItem;
            // Check if editing an existing item with valid ID (positive ID from database)
            if (editingItem && editingItem.id && editingItem.id > 0) {
                savedItem = await ProtocolService.updateItem(editingItem.id, payload, languageFiles.de.length > 0 || languageFiles.en.length > 0 ? languageFiles : { de: [], en: [] });
                setProtocolItems((prev) =>
                    prev.map((item) => (item.id === editingItem.id ? savedItem : item))
                );
                enqueueSnackbar(t("toasts.successfullyUpdated", { name: itemFormData.text }), { variant: "success" });
            } else {
                // Create new item (either completely new or updating a temporary local item)
                savedItem = await ProtocolService.createItem(Number(protocolId), payload, languageFiles.de.length > 0 || languageFiles.en.length > 0 ? languageFiles : { de: [], en: [] });
                if (editingItem) {
                    // Replace the temporary item with the saved one
                    setProtocolItems((prev) =>
                        prev.map((item) => (item.id === editingItem.id ? savedItem : item))
                    );
                } else {
                    // Add new item
                    setProtocolItems((prev) => [...prev, savedItem]);
                }
                enqueueSnackbar(t("toasts.successfullyCreated", { name: itemFormData.text }), { variant: "success" });
            }

            setIsItemModalOpen(false);
            setItemFormData(initialItemFormData);
            setLanguageFiles({ de: [], en: [] });
            setEditingItem(null);
        } catch (error) {
            handleError(error);
        } finally {
            setIsSavingItem(false);
        }
    };

    const onSubmit = async (data: IProtocolForm) => {
        try {
            if (protocolId) {
                await ProtocolService.update({ ...data, id: Number(protocolId) }, Number(protocolId));
                enqueueSnackbar(t("toasts.successfullyUpdated", { name: data.title }), { variant: "success" });
                // Stay on edit page after update
            } else {
                const created = await ProtocolService.create(data);

                // Save any locally created items to the new protocol
                if (protocolItems.length > 0) {
                    for (const item of protocolItems) {
                        const payload = {
                            text: item.text,
                            mediaType: item.mediaType,
                            protocolId: created.id!,
                            contents: item.contents,
                        };
                        await ProtocolService.createItem(created.id!, payload);
                    }
                }

                enqueueSnackbar(t("toasts.successfullyCreated", { name: data.title }), { variant: "success" });
                // Navigate to protocols list
                navigate("/protocols");
            }
        } catch (error) {
            handleError(error);
        }
    };

    const handleCancel = () => {
        navigate("/protocols");
    };

    // Handle file selection for language content (supports multiple files)
    const handleFileChange = (lang: string, e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (files && files.length > 0) {
            const fileArray = Array.from(files);
            setLanguageFiles((prev) => ({
                ...prev,
                [lang]: [...prev[lang], ...fileArray],
            }));
        }
    };

    // Remove a specific file from language files
    const handleRemoveFile = (lang: string, fileIndex: number) => {
        setLanguageFiles((prev) => ({
            ...prev,
            [lang]: prev[lang].filter((_, index) => index !== fileIndex),
        }));
    };

    // Handle media type change - clear files when switching types
    const handleMediaTypeChange = (newType: MediaType) => {
        if (newType !== itemFormData.mediaType) {
            setLanguageFiles({ de: [], en: [] });
            setItemFormData((prev) => ({ ...prev, mediaType: newType }));
        }
    };

    // Get icon for media type
    const getMediaTypeIcon = (mediaType: MediaType) => {
        switch (mediaType) {
            case "TEXT":
                return "ti ti-file-text";
            case "VIDEO":
                return "ti ti-video";
            case "IMAGE":
                return "ti ti-photo";
            default:
                return "ti ti-file";
        }
    };

    // Render item card - matching wireframe design
    const renderItemCard = (item: IProtocolItem, index: number) => (
        <Draggable draggableId={String(item.id)} index={index} key={item.id}>
            {(provided, snapshot) => (
                <Card
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    className={`mb-3 border ${snapshot.isDragging ? "shadow-lg" : ""}`}
                >
                    <Card.Header className="d-flex justify-content-between align-items-center py-2 bg-light border-bottom">
                        <div className="d-flex align-items-center">
                            <span
                                {...provided.dragHandleProps}
                                className="me-2"
                                style={{ cursor: "grab" }}
                            >
                                <i className="ti ti-grip-vertical f-18 text-muted"></i>
                            </span>
                            <span className="badge bg-dark px-3 py-2">
                                {item.mediaType}
                            </span>
                        </div>
                        <div>
                            <Button
                                variant="link"
                                size="sm"
                                className="p-1 text-dark"
                                onClick={() => handleEditItem(item)}
                            >
                                <i className="ti ti-pencil f-18"></i>
                            </Button>
                            <Button
                                variant="link"
                                size="sm"
                                className="p-1 text-dark"
                                onClick={() => handleDeleteItem(item.id!)}
                            >
                                <i className="ti ti-x f-18"></i>
                            </Button>
                        </div>
                    </Card.Header>
                    <Card.Body>
                            {item.mediaType === "TEXT" && (
                                <div
                                    className="small"
                                    dangerouslySetInnerHTML={{
                                        __html: (() => {
                                            const currentLang = i18n.language === "de" ? "de" : "en";
                                            return item.contents?.[currentLang]?.text || item.contents?.de?.text || item.contents?.en?.text || item.text || "-";
                                        })(),
                                    }}
                                />
                            )} 
                            {item.mediaType === "VIDEO" && (
                                <div className="text-center py-3">
                                    {getMediaUrl(item) ? (
                                        <video
                                            controls
                                            className="w-100"
                                            style={{ maxHeight: "200px", cursor: "pointer" }}
                                        >
                                            <source src={getMediaUrl(item)!} type="video/mp4" />
                                            Your browser does not support the video tag.
                                        </video>
                                    ) : (
                                        <i className="ti ti-brand-youtube f-48 text-dark"></i>
                                    )}
                                </div>
                            )}
                            {item.mediaType === "IMAGE" && (
                                <div className="text-center py-3">
                                    {(() => {
                                        const urls = getAllMediaUrls(item);
                                        if (urls.length === 0) {
                                            return <i className="ti ti-photo f-48 text-muted"></i>;
                                        }
                                        if (urls.length === 1) {
                                            return (
                                                <img
                                                    src={urls[0]}
                                                    alt={t("general.preview")}
                                                    className="img-fluid rounded"
                                                    style={{ maxHeight: "200px", cursor: "pointer" }}
                                                />
                                            );
                                        }
                                        // Multiple images - show grid
                                        return (
                                            <div className="position-relative">
                                                <div className="d-flex flex-wrap gap-2 justify-content-center">
                                                    {urls.slice(0, 4).map((url, idx) => (
                                                        <img
                                                            key={idx}
                                                            src={url}
                                                            alt={`Preview ${idx + 1}`}
                                                            className="rounded"
                                                            style={{ 
                                                                width: "90px", 
                                                                height: "90px", 
                                                                objectFit: "cover",
                                                                cursor: "pointer"
                                                            }}
                                                        />
                                                    ))}
                                                </div>
                                                {urls.length > 4 && (
                                                    <div 
                                                        className="badge bg-dark position-absolute"
                                                        style={{ 
                                                            top: "10px", 
                                                            right: "10px",
                                                            fontSize: "12px",
                                                            padding: "6px 10px"
                                                        }}
                                                    >
                                                        +{urls.length - 4} more
                                                    </div>
                                                )}
                                                <div 
                                                    className="badge bg-primary position-absolute"
                                                    style={{ 
                                                        top: "10px", 
                                                        left: "10px",
                                                        fontSize: "12px",
                                                        padding: "6px 10px"
                                                    }}
                                                >
                                                    <i className="ti ti-photo me-1"></i>
                                                    {t(`protocol.imageCount${urls.length !== 1 ? '_plural' : ''}`, { count: urls.length })}
                                                </div>
                                            </div>
                                        );
                                    })()}
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                )}
            </Draggable>
    );

    // Get media URLs for an item in the current language (with fallback)
    const getAllMediaUrls = (item: IProtocolItem): string[] => {
        const currentLang = i18n.language === "de" ? "de" : "en";
        const fallbackLang = currentLang === "de" ? "en" : "de";
        
        let contents = item.contents?.[currentLang]?.files || [];
        // Fallback to other language if current has no files
        if (contents.length === 0) {
            contents = item.contents?.[fallbackLang]?.files || [];
        }
        
        const urls: string[] = [];
        contents.forEach(content => {
            if (content?.contentUrl) {
                let url = content.contentUrl;
                
                // If already a full URL, use as-is
                if (url.startsWith('http://') || url.startsWith('https://')) {
                    if (!urls.includes(url)) {
                        urls.push(url);
                    }
                } else if (url.startsWith('/')) {
                    // Convert relative URL to full URL
                    const baseUrlWithoutApi = (BASE_URL || '').replace(/\/api\/v\d+/, '');
                    const fullUrl = `${baseUrlWithoutApi}${url}`;
                    if (!urls.includes(fullUrl)) {
                        urls.push(fullUrl);
                    }
                }
            }
        });
        
        return urls;
    };

    // Mobile preview component - matching SafeSignal mobile app design
    const MobilePreview = () => (
        <div
            className="border border-2 rounded-4 bg-dark p-2"
            style={{ width: "320px", minHeight: "580px", borderColor: "#1a1a2e" }}
        >
            {/* Phone screen */}
            <div
                className="bg-white rounded-3 overflow-auto px-3 py-4"
                style={{ minHeight: "560px", maxHeight: "560px" }}
            >
                {/* Safety Procedures Title */}
                <h5 
                    className="mb-4" 
                    style={{ 
                        color: "#1a1a2e", 
                        fontWeight: 800, 
                        fontSize: "18px", 
                        letterSpacing: "0.5px",
                        textTransform: "uppercase"
                    }}
                >
                    SAFETY PROCEDURES
                </h5>

                {/* Incident Alert Card */}
                <div 
                    className="mb-4 p-3 d-flex align-items-start"
                    style={{ 
                        backgroundColor: "#c62828", 
                        borderRadius: "12px",
                        border: "2px dashed rgba(255,255,255,0.3)"
                    }}
                >
                    <div 
                        className="d-flex align-items-center justify-content-center me-3 flex-shrink-0"
                        style={{ 
                            width: "44px", 
                            height: "44px", 
                            backgroundColor: "rgba(255,255,255,0.2)", 
                            borderRadius: "8px" 
                        }}
                    >
                        <i className="ti ti-alert-triangle text-white" style={{ fontSize: "24px" }}></i>
                    </div>
                    <div>
                        <div className="text-white fw-bold" style={{ fontSize: "13px", lineHeight: 1.3 }}>
                            Active shooter in progress
                        </div>
                        <div className="text-white-50" style={{ fontSize: "10px", marginTop: "4px" }}>
                            Reported by <span className="text-white fw-semibold">Peter</span> from <span className="text-white fw-semibold">School</span>
                        </div>
                        <div className="text-white-50" style={{ fontSize: "10px" }}>Place: Flor 2</div>
                        <div className="text-white-50" style={{ fontSize: "10px" }}>Reported 3 times</div>
                    </div>
                </div>

                {/* Protocol Steps */}
                {protocolItems.length === 0 ? (
                    <p className="text-muted text-center small mt-4">
                        {t("protocol.noItemsPreview")}
                    </p>
                ) : (
                    (() => {
                        let titleCounter = 0;
                        return protocolItems.map((item) => {
                            // Get current language from i18n
                            const currentLang = i18n.language === "de" ? "de" : "en";
                            const hasTitle = item.mediaType === "TEXT" && item.text && item.text.trim() !== "";
                            
                            // Increment counter only for TEXT items with titles
                            if (hasTitle) {
                                titleCounter++;
                            }
                            const currentNumber = titleCounter;
                            
                            return (
                            <div key={item.id} className="mb-2">
                                {item.mediaType === "TEXT" && (
                                    <div className="d-flex align-items-start">
                                        {/* Step Number */}
                                        {hasTitle && (
                                            <span 
                                                className="flex-shrink-0"
                                                style={{ 
                                                    color: "#c62828", 
                                                    fontWeight: 800, 
                                                    fontSize: "18px",
                                                    lineHeight: "22px",
                                                    marginRight: "0",
                                                    minWidth: "24px"
                                                }}
                                            >
                                                {currentNumber}.
                                            </span>
                                        )}
                                        {/* Title + Description */}
                                        <div className="flex-grow-1">
                                            {hasTitle && (
                                                <div 
                                                    style={{ 
                                                        fontSize: "18px", 
                                                        fontWeight: 800,
                                                        color: "#1a1a2e",
                                                        lineHeight: "22px",
                                                        letterSpacing: "0.3px"
                                                    }}
                                                >
                                                    {item.text}
                                                </div>
                                            )}
                                            <div
                                                style={{ 
                                                    fontSize: "18px", 
                                                    lineHeight: "1.5",
                                                    color: "#666"
                                                }}
                                                className="protocol-preview-text"
                                                dangerouslySetInnerHTML={{
                                                    __html: (item.contents?.[currentLang]?.text || item.contents?.de?.text || item.contents?.en?.text || "")
                                                        .replace(/<strong>/gi, '<strong style="font-weight: 700 !important; color: #333 !important;">')
                                                        .replace(/<b>/gi, '<b style="font-weight: 700 !important; color: #333 !important;">')
                                                        .replace(/<em>/gi, '<em style="font-style: italic !important;">')
                                                        .replace(/<i>/gi, '<i style="font-style: italic !important;">')
                                                        .replace(/<u>/gi, '<u style="text-decoration: underline !important;">')
                                                }}
                                            />
                                        </div>
                                    </div>
                            )}
                            {item.mediaType === "VIDEO" && (
                                <div>
                                    <div 
                                        className="position-relative rounded overflow-hidden"
                                        style={{ 
                                            borderRadius: "8px",
                                            cursor: "pointer"
                                        }}
                                    >
                                        {(() => {
                                            const videoUrl = getMediaUrl(item);
                                            return videoUrl ? (
                                            <>
                                                <video
                                                    className="w-100"
                                                    style={{ 
                                                        maxHeight: "160px", 
                                                        objectFit: "cover",
                                                        display: "block",
                                                        borderRadius: "8px"
                                                    }}
                                                >
                                                    <source src={videoUrl} type="video/mp4" />
                                                </video>
                                                {/* Play Button Overlay */}
                                                <div 
                                                    className="position-absolute top-50 start-50 translate-middle d-flex align-items-center justify-content-center"
                                                    style={{ 
                                                        width: "56px", 
                                                        height: "56px", 
                                                        backgroundColor: "#ffffff",
                                                        borderRadius: "50%",
                                                        boxShadow: "0 3px 12px rgba(0,0,0,0.25)"
                                                    }}
                                                >
                                                    <div 
                                                        className="d-flex align-items-center justify-content-center"
                                                        style={{
                                                            width: "100%",
                                                            height: "100%"
                                                        }}
                                                    >
                                                        <svg 
                                                            width="24" 
                                                            height="24" 
                                                            viewBox="0 0 24 24" 
                                                            fill="none" 
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            style={{ marginLeft: "3px" }}
                                                        >
                                                            <path 
                                                                d="M8 5.14v13.72L19 12L8 5.14z" 
                                                                fill="#c62828"
                                                            />
                                                        </svg>
                                                    </div>
                                                </div>
                                            </>
                                        ) : (
                                            <div 
                                                className="d-flex align-items-center justify-content-center bg-light"
                                                style={{ height: "120px", borderRadius: "8px" }}
                                            >
                                                <i className="ti ti-video text-muted" style={{ fontSize: "32px" }}></i>
                                            </div>
                                        );
                                        })()}
                                    </div>
                                </div>
                            )}
                            {item.mediaType === "IMAGE" && (
                                <div>
                                    {(() => {
                                        const urls = getAllMediaUrls(item);
                                        
                                        if (urls.length === 0) {
                                            return (
                                                <div 
                                                    className="d-flex align-items-center justify-content-center bg-light"
                                                    style={{ height: "80px", borderRadius: "8px" }}
                                                >
                                                    <i className="ti ti-photo text-muted" style={{ fontSize: "32px" }}></i>
                                                </div>
                                            );
                                        }
                                        
                                        // Stacked images preview (carousel-style)
                                        return (
                                            <div 
                                                className="position-relative"
                                                style={{ 
                                                    borderRadius: "12px",
                                                    overflow: "hidden"
                                                }}
                                            >
                                                {/* Main image */}
                                                <img
                                                    src={urls[0]}
                                                    alt={t("general.preview")}
                                                    style={{ 
                                                        width: "100%", 
                                                        height: "auto", 
                                                        display: "block",
                                                        borderRadius: "12px"
                                                    }}
                                                />
                                                {/* Stacked cards effect behind */}
                                                {urls.length > 1 && (
                                                    <>
                                                        <div 
                                                            className="position-absolute"
                                                            style={{ 
                                                                top: "6px",
                                                                left: "6px",
                                                                right: "-6px",
                                                                bottom: "-6px",
                                                                backgroundColor: "rgba(0,0,0,0.15)",
                                                                borderRadius: "12px",
                                                                zIndex: -1
                                                            }}
                                                        />
                                                        {urls.length > 2 && (
                                                            <div 
                                                                className="position-absolute"
                                                                style={{ 
                                                                    top: "12px",
                                                                    left: "12px",
                                                                    right: "-12px",
                                                                    bottom: "-12px",
                                                                    backgroundColor: "rgba(0,0,0,0.08)",
                                                                    borderRadius: "12px",
                                                                    zIndex: -2
                                                                }}
                                                            />
                                                        )}
                                                    </>
                                                )}
                                                {/* Image count badge */}
                                                {urls.length > 1 && (
                                                    <div 
                                                        className="position-absolute d-flex align-items-center justify-content-center"
                                                        style={{ 
                                                            bottom: "8px",
                                                            right: "8px",
                                                            backgroundColor: "rgba(0,0,0,0.6)",
                                                            color: "#fff",
                                                            borderRadius: "16px",
                                                            padding: "4px 10px",
                                                            fontSize: "11px",
                                                            fontWeight: 600
                                                        }}
                                                    >
                                                        <i className="ti ti-photo me-1" style={{ fontSize: "12px" }}></i>
                                                        {urls.length}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })()}
                                </div>
                            )}
                            </div>
                            );
                        });
                    })()
                )}
            </div>
        </div>
    );

    // Render file upload section (supports multiple files)
    const renderFileUpload = (
        lang: string,
        files: File[],
        existingContent: IProtocolItemContent | undefined,
        fileInputRef: React.RefObject<HTMLInputElement>,
        onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void,
        onRemoveFile: (index: number) => void
    ) => {
        const acceptTypes = itemFormData.mediaType === "IMAGE" ? "image/*" : "video/*";
        const hasExistingFiles = existingContent?.files && existingContent.files.length > 0;

        return (
            <div className="border rounded p-3">
                <input
                    ref={fileInputRef}
                    type="file"
                    accept={acceptTypes}
                    multiple
                    onChange={onFileChange}
                    className="d-none"
                />
                
                {/* Display selected files */}
                {files.length > 0 && (
                    <div className="mb-3">
                        {files.map((file, index) => (
                            <div key={index} className="d-flex align-items-center justify-content-between border-bottom py-2">
                                <div className="d-flex align-items-center">
                                    <i className={`${getMediaTypeIcon(itemFormData.mediaType)} f-20 text-success me-2`}></i>
                                    <span className="small">{file.name}</span>
                                </div>
                                <Button
                                    variant="link"
                                    size="sm"
                                    className="p-0 text-danger"
                                    onClick={() => onRemoveFile(index)}
                                >
                                    <i className="ti ti-x"></i>
                                </Button>
                            </div>
                        ))}
                    </div>
                )}
                
                {/* Display existing files from server */}
                {hasExistingFiles && (
                    <div className="mb-3">
                        <p className="small text-muted mb-2">{t("protocol.existingFiles")}:</p>
                        {existingContent.files!.map((content, index) => (
                            <div key={index} className="d-flex align-items-center border-bottom py-2">
                                <i className={`${getMediaTypeIcon(itemFormData.mediaType)} f-20 text-primary me-2`}></i>
                                <span className="small">{content.contentName || t("protocol.fileNumber", { number: index + 1 })}</span>
                            </div>
                        ))}
                    </div>
                )}
                
                {/* Upload button/dropzone */}
                <div
                    className="text-center cursor-pointer p-3 bg-light rounded"
                    onClick={() => fileInputRef.current?.click()}
                    style={{ cursor: "pointer" }}
                >
                    <i className={`${getMediaTypeIcon(itemFormData.mediaType)} f-32 text-muted mb-2 d-block`}></i>
                    <p className="text-muted small mb-0">
                        {files.length > 0 || hasExistingFiles
                            ? t("protocol.addMoreFiles")
                            : t("protocol.dropFileHere")}
                    </p>
                </div>
            </div>
        );
    };

    return (
        <React.Fragment>
            <BreadcrumbItem
                mainTitle={t("protocol.protocols")}
                subTitle={protocolId ? t("protocol.updateProtocol") : t("protocol.createProtocol")}
            />

            <Row>
                {/* Main Form */}
                <Col lg={8} xl={9}>
                    <Form onSubmit={handleSubmit(onSubmit)}>
                        <Card>
                            <Card.Header>
                                <h5>{t("protocol.protocolInformation")}</h5>
                            </Card.Header>
                            <Card.Body>
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3" controlId="title">
                                            <Form.Label>{t("protocol.procedureName")}</Form.Label>
                                            <Controller
                                                name="title"
                                                control={control}
                                                render={({ field }) => (
                                                    <Form.Control
                                                        {...field}
                                                        type="text"
                                                        placeholder={t("protocol.procedureNamePlaceholder")}
                                                        isInvalid={!!errors.title?.message}
                                                    />
                                                )}
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {errors.title?.message && t(errors.title.message as string)}
                                            </Form.Control.Feedback>
                                        </Form.Group>

                                        <Form.Group className="mb-3" controlId="incidentType">
                                            <Form.Label>{t("incident.incidentType")}</Form.Label>
                                            <Controller
                                                name="incidentType"
                                                control={control}
                                                render={({ field }) => (
                                                    <Select2
                                                        {...field}
                                                        name="incidentType"
                                                        placeholder={t("incident.selectIncidentType")}
                                                        options={incidentTypeOptions}
                                                        onChange={(value) => field.onChange(value)}
                                                        value={field.value}
                                                    />
                                                )}
                                            />
                                        </Form.Group>

                                        <Form.Group className="mb-3" controlId="institutionId">
                                            <Form.Label>{t("institution.institution")}</Form.Label>
                                            <Controller
                                                name="institutionId"
                                                control={control}
                                                render={({ field }) => (
                                                    <AsyncSelect2
                                                        name="institutionId"
                                                        placeholder={t("institution.selectInstitution")}
                                                        loadOptions={loadInstitutions}
                                                        defaultOptions={defaultInstitutionOptions}
                                                        value={field.value}
                                                        onChange={(value) => field.onChange(value)}
                                                        error={
                                                            errors.institutionId?.message
                                                                ? t(errors.institutionId.message as string)
                                                                : undefined
                                                        }
                                                    />
                                                )}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3" controlId="note">
                                            <Form.Label>{t("general.note")}</Form.Label>
                                            <Controller
                                                name="note"
                                                control={control}
                                                render={({ field }) => (
                                                    <Form.Control
                                                        {...field}
                                                        as="textarea"
                                                        rows={8}
                                                        placeholder={t("protocol.notePlaceholder")}
                                                        isInvalid={!!errors.note?.message}
                                                    />
                                                )}
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {errors.note?.message && t(errors.note.message as string)}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </Card.Body>
                            <Card.Footer className="text-end">
                                <Button variant="secondary" type="button" onClick={handleCancel} className="me-2">
                                    {t("button.cancel")}
                                </Button>
                                <Button variant="primary" type="submit" disabled={isSubmitting}>
                                    {isSubmitting ? t("button.submitting") : t("button.save")}
                                </Button>
                            </Card.Footer>
                        </Card>

                        {/* Procedure Items Section - Only visible in edit mode */}
                        {protocolId && (
                        <Card className="mt-4">
                            <Card.Header className="d-flex justify-content-between align-items-center">
                                <h5 className="mb-0">{t("protocol.procedureItems")}</h5>
                                <Button variant="outline-dark" size="sm" onClick={handleAddItem}>
                                    {t("protocol.addNew").toUpperCase()}
                                </Button>
                            </Card.Header>
                            <Card.Body>
                                {isLoadingItems ? (
                                    <div className="text-center py-5">
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                    </div>
                                ) : protocolItems.length === 0 ? (
                                    <div className="text-center py-5">
                                        <i className="ti ti-clipboard-list f-48 text-muted mb-3 d-block"></i>
                                        <p className="text-muted">{t("protocol.noItemsYet")}</p>
                                        <Button variant="primary" onClick={handleAddItem}>
                                            <i className="ti ti-plus me-1"></i>
                                            {t("protocol.addFirstItem")}
                                        </Button>
                                    </div>
                                ) : (
                                    <DragDropContext onDragEnd={handleDragEnd}>
                                        <Droppable droppableId="protocolItems" direction="vertical">
                                            {(provided) => (
                                                <div
                                                    {...provided.droppableProps}
                                                    ref={provided.innerRef}
                                                >
                                                    {protocolItems.map((item, index) =>
                                                        renderItemCard(item, index)
                                                    )}
                                                    {provided.placeholder}
                                                </div>
                                            )}
                                        </Droppable>
                                    </DragDropContext>
                                )}
                            </Card.Body>
                        </Card>
                        )}
                    </Form>
                </Col>

                {/* Mobile Preview */}
                <Col lg={4} xl={3} className="d-none d-lg-block">
                    <div className="sticky-top" style={{ top: "100px" }}>
                        <h6 className="text-muted mb-3">{t("protocol.mobilePreview")}</h6>
                        <MobilePreview />
                    </div>
                </Col>
            </Row>

            {/* Add/Edit Item Modal */}
            {isItemModalOpen && (
            <Modal show={isItemModalOpen} onHide={() => setIsItemModalOpen(false)} centered size="lg" backdrop="static">
                <Modal.Header closeButton>
                    <Modal.Title>
                        {editingItem ? t("protocol.editItem") : t("protocol.addItem")}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {/* Item Label */}
                    <Form.Group className="mb-3">
                        <Form.Label>{t("protocol.itemLabel")}</Form.Label>
                        <Form.Control
                            type="text"
                            value={itemFormData.text}
                            onChange={(e) => setItemFormData((prev) => ({ ...prev, text: e.target.value }))}
                            placeholder={t("protocol.itemLabelPlaceholder")}
                        />
                    </Form.Group>

                    {/* Item Type Selection */}
                    <Form.Group className="mb-3">
                        <Form.Label>{t("protocol.itemType.label")}</Form.Label>
                        <div className="d-flex gap-2">
                            {(["TEXT", "VIDEO", "IMAGE"] as MediaType[]).map((type) => (
                                <Button
                                    key={type}
                                    variant={itemFormData.mediaType === type ? "primary" : "outline-secondary"}
                                    onClick={() => handleMediaTypeChange(type)}
                                    className="flex-fill"
                                >
                                    <i className={`${getMediaTypeIcon(type)} me-1`}></i>
                                    {t(`protocol.itemType.${type.toLowerCase()}`)}
                                </Button>
                            ))}
                        </div>
                    </Form.Group>

                    {/* Language Tabs */}
                    <Tab.Container defaultActiveKey="german">
                        <Nav variant="tabs" className="mb-3">
                            <Nav.Item>
                                <Nav.Link eventKey="german">
                                    <span className="me-1">🇩🇪</span>
                                    {t("protocol.tabGerman")}
                                </Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link eventKey="english">
                                    <span className="me-1">🇬🇧</span>
                                    {t("protocol.tabEnglish")}
                                </Nav.Link>
                            </Nav.Item>
                        </Nav>
                        <Tab.Content>
                            {/* German Content Tab */}
                            <Tab.Pane eventKey="german">
                                {itemFormData.mediaType === "TEXT" ? (
                                    <Form.Group>
                                        <Form.Label>{t("protocol.germanContent")}</Form.Label>
                                        <div style={{ height: "350px" }}>
                                            <ReactQuill
                                                theme="snow"
                                                value={itemFormData.contents?.de?.text || ""}
                                                onChange={(value) =>
                                                    setItemFormData((prev) => ({
                                                        ...prev,
                                                        contents: {
                                                            ...prev.contents,
                                                            de: { ...prev.contents?.de, text: value },
                                                        },
                                                    }))
                                                }
                                                modules={quillModules}
                                                formats={quillFormats}
                                                style={{ height: "300px" }}
                                            />
                                        </div>
                                        <Button
                                            variant="outline-primary"
                                            size="sm"
                                            className="mt-2"
                                            onClick={() => handleAITranslate("de", "en")}
                                            disabled={isTranslating}
                                        >
                                            <i className="ti ti-language me-1"></i>
                                            {isTranslating ? t("protocol.translating") : t("protocol.translateToEnglish")}
                                        </Button>
                                    </Form.Group>
                                ) : (
                                    <Form.Group>
                                        <Form.Label>
                                            {itemFormData.mediaType === "IMAGE"
                                                ? t("protocol.uploadImage")
                                                : t("protocol.uploadVideo")}
                                        </Form.Label>
                                        {renderFileUpload(
                                            "de",
                                            languageFiles.de,
                                            itemFormData.contents?.de,
                                            germanFileInputRef as React.RefObject<HTMLInputElement>,
                                            (e) => handleFileChange("de", e),
                                            (index) => handleRemoveFile("de", index)
                                        )}
                                    </Form.Group>
                                )}
                            </Tab.Pane>

                            {/* English Content Tab */}
                            <Tab.Pane eventKey="english">
                                {itemFormData.mediaType === "TEXT" ? (
                                    <Form.Group>
                                        <Form.Label>{t("protocol.englishContent")}</Form.Label>
                                        <div style={{ height: "350px" }}>
                                            <ReactQuill
                                                theme="snow"
                                                value={itemFormData.contents?.en?.text || ""}
                                                onChange={(value) =>
                                                    setItemFormData((prev) => ({
                                                        ...prev,
                                                        contents: {
                                                            ...prev.contents,
                                                            en: { ...prev.contents?.en, text: value },
                                                        },
                                                    }))
                                                }
                                                modules={quillModules}
                                                formats={quillFormats}
                                                style={{ height: "300px" }}
                                            />
                                        </div>
                                        <Button
                                            variant="outline-primary"
                                            size="sm"
                                            className="mt-2"
                                            onClick={() => handleAITranslate("en", "de")}
                                            disabled={isTranslating}
                                        >
                                            <i className="ti ti-language me-1"></i>
                                            {isTranslating ? t("protocol.translating") : t("protocol.translateToGerman")}
                                        </Button>
                                    </Form.Group>
                                ) : (
                                    <Form.Group>
                                        <Form.Label>
                                            {itemFormData.mediaType === "IMAGE"
                                                ? t("protocol.uploadImage")
                                                : t("protocol.uploadVideo")}
                                        </Form.Label>
                                        {renderFileUpload(
                                            "en",
                                            languageFiles.en,
                                            itemFormData.contents?.en,
                                            englishFileInputRef as React.RefObject<HTMLInputElement>,
                                            (e) => handleFileChange("en", e),
                                            (index) => handleRemoveFile("en", index)
                                        )}
                                    </Form.Group>
                                )}
                            </Tab.Pane>
                        </Tab.Content>
                    </Tab.Container>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setIsItemModalOpen(false)}>
                        {t("button.cancel")}
                    </Button>
                    <Button variant="primary" onClick={handleSaveItem} disabled={isSavingItem}>
                        {isSavingItem ? t("button.submitting") : t("button.save")}
                    </Button>
                </Modal.Footer>
            </Modal>
            )}
        </React.Fragment>
    );
};

export default ProtocolForm;
